<template>
  <div class="contest-detail">
    <vue-loading v-if="isLoading" type="spiningDubbles" color="#efad4c" :size="{ width: '50px', height: '50px' }" />

    <template v-else>
      <div class="container">
        <div class="image-container">
          <img :src="image" alt="image" width="100%" height="auto" loading="lazy" />
        </div>
        <div v-if="referenceSource" class="reference-source">{{ $t('eventContestDetail.referenceSourceLabel') }}{{
          referenceSource }}</div>
        <div class="text-container">
          <div class="content" v-html="$sanitize(content)" />
        </div>
        <img class="cat-hr" src="@/assets/img/event/contest/cat_hr.png" />
      </div>
      <div class="side-container">
        <div class="title">{{ title }}</div>
        <div v-if="organizer" class="organizer">{{ organizer }}</div>
        <div v-if="free" class="free">{{ free }}</div>
        <template v-if="themeName">
          <div class="label">{{ $t('eventContestDetail.themeLabel') }}</div>
          <div class="summary">
            {{ themeName }}
          </div>
        </template>
        <div class="label">{{ $t('eventContestDetail.deadlineLabel') }}</div>
        <div class="summary">
          {{ deadline }}
        </div>
        <div class="spacer" />
        <div v-if="requireNolaNovelRegistration" class="require-nola-novel-registration">
          <div>{{ $t('eventContestDetail.requireRegistrationMessage') }}</div>
          <button class="apply-button" @click="navigateToNolaNovel">{{ $t('eventContestDetail.registerAndApplyButton')
            }}</button>
        </div>
        <button v-else-if="entryForm || contestId" class="apply-button" :disabled="isExpired" @click="onClickEntry">
          {{ $t('eventContestDetail.applyButton') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { VueLoading } from "vue-loading-template";
import { showNotifyDialog } from "@/lib/dialog";
import { compareAsc, format } from "date-fns";
import { getDateFnsLocale } from "@/lib/utils/i18n/locale";
import axiosBase from "axios";
import { UserClient } from "@/lib/clients";
import { NolaPageName, NolaItemId } from "@/lib/utils/analytics";
import { NolaNovelUrlGenerator } from "@/lib/utils/generator/nolanovelUrl";
import { createUrlWithUtmParams, optimizeImage } from "@/lib/utils/url";
import { retryableGet } from "@/lib/utils/networkRetry";

const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    VueLoading,
  },
  props: {
    id: String,
  },
  async mounted() {
    // microCMSから返却されたHTMLのタグにスタイルを適用する為に、コンポーネントがマウントされたときにスタイルを追加する
    this.addGlobalStyle();
  },
  beforeDestroy() {
    // 本コンポーネントで追加したHTMLタグに対するスタイルが、他のコンポーネントに影響しないように
    // コンポーネントが破棄される前にスタイルを削除する
    this.removeGlobalStyle();
  },
  async created() {
    const { id } = this;

    const contestRequest = await retryableGet(axios, `/contest/${id}`);
    if (contestRequest.status !== 200) {
      await showNotifyDialog({
        title: this.$t('eventContest.error.title'),
        content: this.$t('eventContest.error.fetchFailed'),
      });
    }
    this.contest = contestRequest.data;
    this.isLoading = false;
  },
  data() {
    return {
      isLoading: true,
      contest: {},
      requireNolaNovelRegistration: false,
    };
  },
  computed: {
    contestId() {
      const { contest } = this;
      return contest.contestId;
    },
    title() {
      const { contest } = this;
      return contest.contest;
    },
    department() {
      const { contest } = this;
      return contest.department;
    },
    free() {
      const { contest } = this;
      return contest.free;
    },
    image() {
      const { contest } = this;
      return optimizeImage(`${contest.image.url}`);
    },
    referenceSource() {
      const { contest } = this;
      return contest.referenceSource;
    },
    deadline() {
      const { contest } = this;
      const { deadline } = contest;

      if (!deadline) return "-";

      const locale = getDateFnsLocale(this.$i18n.locale);
      const formatStr = this.$t("common.deadlineFormat") as string;
      return format(deadline, formatStr, { locale });
    },
    genre() {
      const { contest } = this;
      return contest.genre;
    },
    category() {
      const { contest } = this;
      return contest.category;
    },
    award() {
      const { contest } = this;
      return contest.award;
    },
    content() {
      const { contest } = this;
      return contest.content;
    },
    homepage() {
      const { contest } = this;
      return contest.homepage;
    },
    entryForm() {
      const { contest } = this;
      return contest.entryForm;
    },
    isExpired() {
      const { contest } = this;
      const { deadline } = contest;

      if (!deadline) return false;

      const currentDate = format(new Date(), "YYYY-MM-DD");
      const contestDeadline = format(deadline, "YYYY-MM-DD");

      return compareAsc(currentDate, contestDeadline) === 1;
    },
    organizer() {
      const { contest } = this;
      return contest.organizer ? `${this.$t('eventContestDetail.organizerLabel')}${contest.organizer}` : undefined;
    },
    themeName() {
      const { contest } = this;
      return contest.themeName;
    },
  },
  methods: {
    onClickHomepage() {
      const { homepage } = this;
      window.open(homepage);
    },
    onClickEntry() {
      const { id, contestId, entryForm } = this;

      // エージェント連携CPかどうかで動線を変える
      if (contestId) {
        if (this.contest.isAgentCampaign) {
          this.$router.push({ name: "eventAgentEntry", params: { id, campaignId: contestId } });
        } else {
          this.$router.push({ name: "eventContestEntry", params: { id, contestId } });
        }
      } else {
        window.open(entryForm);
      }
    },
    addGlobalStyle() {
      const style = document.createElement("style");
      style.id = "contest-global-style";
      style.innerHTML = `
        h1 {
          font-size: var(--font-size-lg);
          margin: 30px 0 20px;
        }

        h2 {
          font-size: var(--font-size-lg);
          margin: 30px auto;
          color: #e26762;
          text-align: center;
          border-bottom: 1px solid #e26762;
          line-height: 1.2;
          width: fit-content;
        }

        h3 {
          font-size: var(--font-size-lg);
          margin: 30px 0 15px;
          color: var(--color-text-normal);
          border-bottom: 3px solid var(--color-border);
          padding-bottom: 5px;
        }

        h4 {
          font-size: var(--font-size-md);
          margin: 20px 0 0;
          color: var(--color-text-normal);
          line-height: 1.8;
        }

        h5 {
          font-size: var(--font-size-sm);
          margin: 16px 0 10px;
          line-height: 1.8;
          color: var(--color-text-normal);
        }

        p {
          font-size: var(--font-size-sm);
          margin-bottom: 16px;
          color: var(--color-text-normal);
        }

        pre {
          font-size: var(--font-size-sm);
          padding: 16px;
          margin: 16px 0;
          color: var(--color-text-normal);
          background-color: var(--color-surface-subtle);
          border-radius: var(--radius-xs);
        }

        ul {
          padding-inline-start: 20px;
          margin-block-start: 16px;
          margin-block-end: 16px;
          line-height: 1.8;
        }
      `;
      document.head.appendChild(style);
    },
    removeGlobalStyle() {
      const style = document.getElementById("contest-global-style");
      if (style) {
        style.remove();
      }
    },
    async navigateToNolaNovel() {
      const url = process.env.VUE_APP_NOLANOVEL_WEB;

      if (!url) {
        throw new Error("NolaノベルのWebサイトURLが環境変数に含まれていません。");
      }

      const service = new NolaNovelUrlGenerator(process.env.VUE_APP_NOLANOVEL_WEB!);
      const aboutUrl = await service.genAboutUrl();

      window.open(
        createUrlWithUtmParams(aboutUrl, NolaPageName.Detail, NolaItemId.UseNolaNovelButton),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
    },
  },
  watch: {
    async contestId(value) {
      if (value && !this.contest.isAgentCampaign) {
        const result = await this.$store.dispatch("nolaContestModule/fetchContest", this.contestId);

        // Nolaノベルコンテストの場合はアカウント検証
        if (result.platform === "nolaNovel") {
          this.$store.dispatch("nolaContestModule/fetchContest", this.contestId);
          const userClient = new UserClient();
          const results = await userClient.checkConnectedOtherServices();
          this.requireNolaNovelRegistration = !results.nolaNovel.result;
        }
      }
    },
  },
});

interface Props {
  id: string;
}

interface Data {
  isLoading: boolean;
  contest: Contest;
  requireNolaNovelRegistration: boolean;
}

interface Computed {
  contestId?: string;
  title: string;
  department: string;
  free: string;
  image?: string;
  referenceSource: string;
  deadline: string;
  genre: string;
  category: string;
  award: string;
  content: string;
  homepage: string;
  entryForm: string;
  isExpired: boolean;
  organizer?: string;
  themeName?: string;
}

interface Methods {
  onClickHomepage(): void;
  onClickEntry(): void;
  addGlobalStyle(): void;
  removeGlobalStyle(): void;
  navigateToNolaNovel(): Promise<void>;
}

type Contest = {
  [key: string]: any;
};
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.contest-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--color-background-normal);
  overflow: hidden;

  .container {
    height: 100%;
    background-color: var(--color-surface-subtle);
    overflow-y: auto;
    margin-left: 2%;
    margin-right: 2%;
    flex: 1;
    color: var(--color-text-normal);
  }

  .image-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .reference-source {
    text-align: right;
    margin: 0.5em 0.5em 0;
  }

  .text-container {
    box-sizing: border-box;
    padding: 1.5em 5em;
    display: flex;
    flex-direction: column;
    align-items: center;

    .content {
      width: 100%;
    }
  }

  .side-container {
    width: 360px;
    height: 100%;
    background-color: var(--color-surface-subtle);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 30px;
    color: var(--color-text-normal);

    .title {
      font-weight: var(--font-weight-bold);
      font-size: large;
      margin: 10px 0;
      color: var(--color-text-normal);
    }

    .organizer {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-bold);
      color: var(--color-text-low);
      border-bottom: 3px solid var(--color-border);
      padding-bottom: 15px;
    }

    .free {
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-sm);
      margin: 15px 0;
      color: var(--color-text-normal);
    }

    .label {
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-sm);
      margin: 30px 0 15px;
      color: var(--color-primary);
    }

    .summary {
      font-weight: var(--font-weight-bold);
      box-sizing: border-box;
      padding: 1.3em;
      background-color: var(--color-surface-muted);
      border-radius: var(--radius-xs);
      font-size: var(--font-size-sm);
      color: var(--color-text-normal);

      * {
        margin-bottom: 0.5em;
        color: var(--color-text-normal);
      }

      *:last-of-type {
        margin-bottom: 0;
      }
    }

    .apply-button {
      position: relative;
      @include hover-overlay;
      box-sizing: border-box;
      padding: 1em;
      border-radius: var(--radius-xs);
      border: none;
      font-weight: var(--font-weight-bold);
      font-size: 1.3em;
      cursor: pointer;
      transition: all 0.3s ease 0s;
      background-color: var(--color-primary);
      color: var(--color-text-on-fixed-bg);
    }


    .apply-button:disabled {
      background-color: var(--color-surface-muted);
      color: var(--color-inactive);
      border: 1px solid var(--color-border);
      cursor: not-allowed;
    }
  }

  .cat-hr {
    width: 100%;
    margin-bottom: 10px;
  }
}

.require-nola-novel-registration {
  display: flex;
  flex-direction: column;
}

.spacer {
  margin-bottom: auto;
}

// markdownコンテンツ用のスタイル
.text-container .content ::v-deep {
  blockquote {
    padding: 5px 10px;
    margin: 0 0 15px;
    border-left: 5px solid var(--color-border);
  }

  code {
    padding: 2px 4px;
    font-size: 90%;
    color: var(--color-text-normal);
    background-color: var(--color-surface-muted);
    border-radius: var(--radius-xs);
  }

  pre {
    display: block;
    padding: 9.5px;
    margin: 0 0 10px;
    font-size: var(--font-size-xs);
    line-height: 1.42857143;
    color: var(--color-text-normal);
    word-break: break-all;
    word-wrap: break-word;
    background-color: var(--color-surface-subtle);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-xs);

    code {
      padding: 0;
      font-size: inherit;
      color: inherit;
      white-space: pre-wrap;
      background-color: transparent;
      border-radius: 0;
    }
  }

  img {
    max-width: 100%;
    height: auto;
  }
}
</style>
